import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLink, ExternalLinkIcon } from 'lucide-react'
import { useMemo } from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { adminRecordInspector } from 'rails_routes'
import { Badge } from 'slab'
import { useGetAdminFinancialApplications } from 'utils/web_api'
import { type AdminFinancialApplication } from 'utils/web_api.schemas'

import FinancialApplicationStateBadge from './FinancialApplicationStateBadge'

interface EmployerApplicationsTableProps {
  employerId: string
}

const getApplicationTypeLabel = (type: string | undefined | null): string => {
  if (!type) return '-'

  const typeMap: Record<string, string> = {
    person_application: 'Person',
    business_application: 'Business',
    account_application: 'Account',
  }

  return typeMap[type] || type
}

const getProviderBadgeType = (provider: string): 'info' | 'thatch' | 'warning' => {
  const providerTypes: Record<string, 'info' | 'thatch' | 'warning'> = {
    unit: 'thatch',
    treasury_prime: 'info',
  }
  return providerTypes[provider] || 'info'
}

const columnHelper = createColumnHelper<AdminFinancialApplication>()

const columns = [
  columnHelper.accessor('id', {
    header: 'ID',
    cell: ({ getValue }) => (
      <Badge
        type='info'
        actionIcon={ExternalLink}
        onActionClick={() => {
          window.open(adminRecordInspector.show({ id: getValue() }), '_blank')
        }}
      >
        {getValue().slice(-6)}
      </Badge>
    ),
  }),
  columnHelper.accessor('application_type', {
    header: 'Application Type',
    cell: ({ getValue }) => getApplicationTypeLabel(getValue()),
  }),
  columnHelper.accessor('provider', {
    header: 'Provider',
    cell: ({ getValue }) => {
      const provider = getValue()
      return (
        <Badge type={getProviderBadgeType(provider)}>
          {provider === 'treasury_prime' ? 'Treasury Prime' : provider.charAt(0).toUpperCase() + provider.slice(1)}
        </Badge>
      )
    },
  }),
  columnHelper.accessor('state', {
    header: 'State',
    cell: ({ getValue }) => <FinancialApplicationStateBadge state={getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const remoteLink = row.original.remote_link

      if (!remoteLink) {
        return <span className='text-gray-400'>-</span>
      }

      return (
        <div className='flex item-center justify-start space-x-2 text-base'>
          <DisplayTooltipOnHover text='View in provider dashboard'>
            <ExternalLinkIcon
              size={16}
              strokeWidth={2}
              className='cursor-pointer'
              onClick={() => {
                window.open(remoteLink, '_blank')
              }}
            />
          </DisplayTooltipOnHover>
        </div>
      )
    },
  }),
]

const EmployerApplicationsTable = ({ employerId }: EmployerApplicationsTableProps) => {
  const { data, isLoading, error } = useGetAdminFinancialApplications(
    { 'filter[employer_id]': employerId },
    { query: { enabled: !!employerId } }
  )

  const applications = useMemo(() => {
    return data?.data || []
  }, [data])

  const table = useReactTable({
    data: applications,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={!!error}
      noDataMessage='No financial applications found for this employer'
      tableMessageHeightClass='h-14'
      paginationLabelSingular='application'
      paginationLabelPlural='applications'
      loadingSkeletonPageSizeOverride={5}
    />
  )
}

export default EmployerApplicationsTable
